import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/_app/chatbot/conversations/$id")({ component: ConversationDetail });

interface Session {
  id: string;
  started_at: number;
  ended_at: number | null;
  locale: string;
  satisfaction: number | null;
}
interface Message {
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  citations_json: string | null;
  ts: number;
}
interface Citation { id: string; title: string; source_url?: string }

function ConversationDetail() {
  const { id } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["chatbot", "conversations", id],
    queryFn: () => api.get<{ session: Session; messages: Message[] }>(`/admin/chatbot/conversations/${id}`),
  });

  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Session ${id.slice(0, 8)}…`}
        back={{ href: "/chatbot/conversations", label: "Conversations" }}
      />

      <Card>
        <CardContent className="grid grid-cols-2 gap-4 pt-6 text-sm md:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Started</p><p>{format(data.session.started_at * 1000, "MMM d, yyyy HH:mm")}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Duration</p><p>{data.session.ended_at ? `${Math.round((data.session.ended_at - data.session.started_at) / 60)} min` : "ongoing"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Locale</p><Badge variant="outline">{data.session.locale}</Badge></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Rating</p><p>{data.session.satisfaction ? "★".repeat(data.session.satisfaction) : "—"}</p></div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {data.messages.map((m, i) => {
          const citations: Citation[] = m.citations_json ? JSON.parse(m.citations_json) : [];
          const isAssistant = m.role === "assistant";
          return (
            <div key={i} className={cn("flex gap-3", isAssistant ? "justify-start" : "justify-end")}>
              <div className={cn(
                "max-w-[80%] rounded-2xl px-4 py-3 text-sm",
                isAssistant ? "bg-secondary" : "bg-primary text-primary-foreground",
              )}>
                <p className="mb-1 text-[10px] uppercase tracking-wider opacity-70">
                  {m.role} · {format(m.ts * 1000, "HH:mm:ss")}
                </p>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {citations.length > 0 && (
                  <div className="mt-3 border-t border-current/10 pt-2">
                    <p className="mb-1.5 text-[10px] uppercase tracking-wider opacity-70">Sources</p>
                    <ul className="space-y-1 text-xs">
                      {citations.map((c, j) => (
                        <li key={j}>
                          [{j + 1}] {c.title}
                          {c.source_url && <a href={c.source_url} className="ml-2 underline opacity-80">view</a>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
